import BoldSmallText from '@/components/BoldSmallText';
import BoldText from '@/components/BoldText';
import { config } from '@/config';
// import { SHOP_USE_PREPOPULATE } from '@/constants/custom-shop.constant';
import { GOOGLE_API_STATUS, WIDGET_DISPLAY_CHOICE, WIDGET_SETTING_CHOICE } from '@/constants/enum';
import { PATH } from '@/constants/path';
import { isEmpty, openCrisp, openToast } from '@/helpers';
import { StyledPadding } from '@/pages/GeneralSetting/styled';
import { useGetGeneralSettingQuery, useLazyCheckGoogleApiKeyQuery, useUpdateGeneralSettingMutation } from '@/redux/query';
import { errorFieldsSelector, handleResetErrorFields, handleSetErrorFields } from '@/redux/slice/error.slice';
import { handleVisibleGGApiBanner, showBannerGGApiSelector } from '@/redux/slice/ggApiBanner.slice';
import {
  generalBackupSettingSelector,
  generalSettingSelector,
  handleSaveBackupSettings,
  handleSaveSettings,
} from '@/redux/slice/setting.slice';
import { Badge, BlockStack, Box, Button, Card, Checkbox, Icon, InlineStack, Link } from '@shopify/polaris';
import { StoreManagedIcon } from '@shopify/polaris-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GoogleAPIModal } from '../../Modal/GoogleAPIModal';
import { handleLocationTab } from '@/redux/slice/tab.slice';
import { Collapsible } from '@/components/Collapsible';
import RegularText from '@/components/RegularText';

export const StorePickup = () => {
  const { t } = useTranslation('general_settings');
  const generalSettings = useSelector(generalSettingSelector);
  const generalSettingsBackup = useSelector(generalBackupSettingSelector);
  const dispatch = useDispatch();
  const [isShowGoogleMapApiModal, setIsShowGoogleMapApiModal] = useState(false);
  const [checkGoogleApiKey, { isFetching: isLoadingCheckGoogleApiKey }] = useLazyCheckGoogleApiKeyQuery();
  const errorFields = useSelector(errorFieldsSelector);
  const showBannerGGApi = useSelector(showBannerGGApiSelector);
  const [updateGeneralSetting, { isLoading: isLoadingGeneralSetting }] = useUpdateGeneralSettingMutation();
  const { refetch: refetchGeneralSettingData } = useGetGeneralSettingQuery({ shop: config.shop });
  const navigate = useNavigate();

  const isExistGoogleMapApiKey = useMemo(
    () => !isEmpty(generalSettingsBackup.googleMapApiKey),
    [generalSettingsBackup.googleMapApiKey],
  );

  const handleChangeGoogleMapApiKey = (value: string) => {
    dispatch(
      handleSetErrorFields({
        errorFields: {
          errorValidGGApi: false,
        },
        isValidAPIKey: false,
      }),
    );
    dispatch(handleSaveSettings({ googleMapApiKey: value }));
  };

  const handleCheckGoogleMapApiKey = () => {
    checkGoogleApiKey({
      shop: config.shop,
      key: generalSettings.googleMapApiKey.trim(),
    })
      .unwrap()
      .then((res) => {
        if (res.data.status === GOOGLE_API_STATUS.INVALID) {
          dispatch(
            handleSetErrorFields({
              errorFields: {
                errorValidGGApi: true,
              },
              isValidAPIKey: false,
            }),
          );
          dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerWarningGoogleAPI', value: true }));
        } else if (res.data.status === GOOGLE_API_STATUS.VALID) {
          dispatch(
            handleSetErrorFields({
              errorFields: {
                errorValidGGApi: false,
              },
              isValidAPIKey: true,
            }),
          );
          dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerValidGoogleAPI', value: true }));
          updateGeneralSetting({
            shop: config.shop,
            googleMapApiKey: generalSettings.googleMapApiKey,
            isEnableGoogleMap: true,
          })
            .unwrap()
            .then(() => {
              openToast(dispatch);
              dispatch(handleSaveSettings({ googleMapApiKey: generalSettings.googleMapApiKey, isEnableGoogleMap: true }));
              dispatch(handleSaveBackupSettings({ googleMapApiKey: generalSettings.googleMapApiKey, isEnableGoogleMap: true }));
              refetchGeneralSettingData();
            })
            .then(() => setIsShowGoogleMapApiModal(false));
        }
      });
  };

  const handleDismissGGApiValid = () => {
    dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerValidGoogleAPI', value: false }));
  };

  const handleDismissGGWarning = () => {
    dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerWarningGoogleAPI', value: false }));
  };

  const handleCheckBoxGoogleMap = () => {
    if (isEmpty(generalSettings.googleMapApiKey) && generalSettings.isEnableGoogleMap === false) {
      setIsShowGoogleMapApiModal(true);
    } else dispatch(handleSaveSettings({ isEnableGoogleMap: !generalSettings.isEnableGoogleMap }));
  };

  return (
    <>
      <Collapsible
        title={
          <Box>
            <InlineStack gap={'200'}>
              <Box>
                <Icon source={StoreManagedIcon} />
              </Box>
              <BoldText>{t('sections.advancedSetting.storePickup.title')}</BoldText>
            </InlineStack>
            <StyledPadding paddingLeft="28px">
              <Box color="text-secondary">
                <RegularText fontWeight="regular">{t('sections.advancedSetting.storePickup.helpText')}</RegularText>
              </Box>
            </StyledPadding>
          </Box>
        }
      >
        <Box paddingBlockStart={'300'}>
          <BlockStack gap={'200'}>
            <Card padding={'300'}>
              <BlockStack gap={'200'}>
                <InlineStack gap="200" blockAlign="center">
                  <BoldText fontWeight="medium">{t('sections.advancedSetting.storePickup.checkoutTitle')}</BoldText>
                  <Badge tone="info">{t('sections.advancedSetting.storePickup.betaFeature')}</Badge>
                </InlineStack>
                <BlockStack gap={'200'}>
                  {generalSettings.displayOnPage.length === 1 &&
                    generalSettings.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.PRODUCT) && (
                      <Box>
                        This feature is only available for those who{' '}
                        <Button
                          variant="plain"
                          onClick={() => {
                            dispatch(handleLocationTab({ key: 'widgetTab', value: WIDGET_SETTING_CHOICE.POSITION }));
                            navigate(PATH.WIDGET.pathname);
                          }}
                        >
                          display the widget on the cart page.
                        </Button>{' '}
                      </Box>
                    )}

                  <Checkbox
                    label={<div className="flex gap-8">{t('sections.advancedSetting.storePickup.checkoutPrepopulateLabel')}</div>}
                    helpText={t('sections.advancedSetting.storePickup.checkoutPrepopulateHelpText')}
                    checked={generalSettings.isAutoFillPickupAddress}
                    onChange={() => {
                      dispatch(handleSaveSettings({ isAutoFillPickupAddress: !generalSettings.isAutoFillPickupAddress }));
                      dispatch(handleSaveSettings({ isModifiedPickupAddress: true }));
                    }}
                    disabled={
                      generalSettings.displayOnPage.length === 1 &&
                      generalSettings.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.PRODUCT)
                    }
                  />
                </BlockStack>
                {generalSettings.isAutoFillPickupAddress && (
                  <StyledPadding paddingLeft="25px">
                    <Checkbox
                      label={t('sections.advancedSetting.storePickup.checkoutCorrectModifiedLabel')}
                      helpText={t('sections.advancedSetting.storePickup.checkoutCorrectModifiedHelpText')}
                      checked={generalSettings.isModifiedPickupAddress}
                      onChange={() =>
                        dispatch(handleSaveSettings({ isModifiedPickupAddress: !generalSettings.isModifiedPickupAddress }))
                      }
                      disabled={
                        generalSettings.displayOnPage.length === 1 &&
                        generalSettings.displayOnPage.includes(WIDGET_DISPLAY_CHOICE.PRODUCT)
                      }
                    />
                  </StyledPadding>
                )}
                <Box color="text-emphasis">
                  <>
                    🔆{' '}
                    <Link
                      onClick={() =>
                        window.open(
                          'https://help.omegatheme.com/en/article/how-to-avoid-customers-switch-from-pickup-to-delivery-in-checkout-page-1xdb41e/?bust=1749608366570',
                          '_blank',
                        )
                      }
                    >
                      View guide
                    </Link>{' '}
                    or{' '}
                  </>
                  <Link onClick={() => openCrisp()}>Contact us</Link>
                </Box>
              </BlockStack>
            </Card>
            <Card padding={'300'}>
              <BlockStack gap={'200'}>
                <BoldText fontWeight="medium">{t('sections.advancedSetting.storePickup.widgetTitle')}</BoldText>
                <InlineStack align="space-between">
                  <Checkbox
                    label={t('sections.advancedSetting.storePickup.widgetLabel')}
                    helpText={t('sections.advancedSetting.storePickup.widgetHelpText')}
                    checked={!!generalSettings.googleMapApiKey && generalSettings.isEnableGoogleMap}
                    onChange={handleCheckBoxGoogleMap}
                  />
                  {isExistGoogleMapApiKey && (
                    <BoldSmallText>
                      <Link removeUnderline onClick={() => setIsShowGoogleMapApiModal(true)}>
                        {t('sections.bannerGoogleAPI.ggApiLink')}
                      </Link>
                    </BoldSmallText>
                  )}
                </InlineStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </Box>
      </Collapsible>
      <GoogleAPIModal
        open={isShowGoogleMapApiModal}
        googleMapApiKey={generalSettings.googleMapApiKey}
        onChange={(value) => handleChangeGoogleMapApiKey(value)}
        onSubmit={handleCheckGoogleMapApiKey}
        onCancel={() => {
          if (!(isLoadingCheckGoogleApiKey || isLoadingGeneralSetting)) {
            dispatch(handleSaveSettings({ googleMapApiKey: generalSettingsBackup.googleMapApiKey }));
            dispatch(handleResetErrorFields());
            setIsShowGoogleMapApiModal(false);
          }
        }}
        isVisible={showBannerGGApi.isShowBannerInfoGoogleAPI}
        loading={isLoadingCheckGoogleApiKey || isLoadingGeneralSetting}
        onDismissInfo={() => dispatch(handleVisibleGGApiBanner({ key: 'isShowBannerInfoGoogleAPI', value: false }))}
        handleDismissSuccess={handleDismissGGApiValid}
        handleDismissWarning={handleDismissGGWarning}
        helpText={t('sections.bannerGoogleAPI.inputHelpText')}
        isErrorShow={errorFields.errorFields?.errorValidGGApi}
        isSuccessShow={errorFields.isValidAPIKey && showBannerGGApi.isShowBannerValidGoogleAPI}
        isWarningBannerShow={showBannerGGApi.isShowBannerWarningGoogleAPI}
      />
    </>
  );
};
